// 실기기 점검(#111). functions/src/qa 응답 형식과 이슈 판정 규칙.

export type Platform = 'android' | 'ios';
export const PLATFORMS: Platform[] = ['android', 'ios'];
export const PLATFORM_LABEL: Record<Platform, string> = { android: 'Android', ios: 'iOS' };

export type QaResultValue = 'pass' | 'fail' | 'skip';
export const RESULT_LABEL: Record<QaResultValue, string> = { pass: '통과', fail: '실패', skip: '건너뜀' };

export interface TcItem {
  id: string;
  title: string;
  steps: string[];
  expect: string;
  ifFail: string;
  required: string[];
  related: string[];
  platforms: Platform[];
  optional: boolean;
  core: boolean;
}

export interface TcStage {
  id: string;
  title: string;
  note: string;
  setup: string[];
  items: TcItem[];
}

export interface TcIssueView {
  key: string;
  repo: string;
  number: number;
  title: string;
  tcUrl: string | null;
  /** 보드 상태 라벨 값(status: …) */
  status: string | null;
  state: 'open' | 'closed' | null;
  url: string | null;
  statusError: string | null;
}

export interface TcView {
  name: string;
  sha: string | null;
  tc: {
    deployment: { date: string | null; builds: { app: string; appId: string | null; pr: number | string | null }[] };
    issues: { key: string }[];
    stages: TcStage[];
  };
  warnings: string[];
  issues: TcIssueView[];
}

export interface QaResult {
  file: string;
  itemId: string;
  platform: Platform;
  result: QaResultValue | null;
  note: string;
  testerId: string;
  updatedAt: string;
}

export const cellKey = (itemId: string, platform: Platform) => `${itemId}|${platform}`;
export type ResultMap = Map<string, QaResult>;
export const toMap = (rs: readonly QaResult[]): ResultMap => new Map(rs.map((r) => [cellKey(r.itemId, r.platform), r]));

export const allItems = (stages: readonly TcStage[]) => stages.flatMap((s) => s.items);

export interface IssueVerdict {
  /** done: Done 가능, failed: 실패 있음, progress: 진행 중, none: required 항목 없음 */
  kind: 'done' | 'failed' | 'progress' | 'none';
  failed: number;
  /** 결과를 기록한 칸 / 전체 칸(항목 × 그 항목의 플랫폼) */
  decided: number;
  total: number;
}

/**
 * 이슈 판정. required로 묶인 항목의 대상 플랫폼 칸을 모두 본다.
 * - 실패가 하나라도 있으면 failed
 * - 모든 칸이 통과면 done. 선택 항목(optional)은 건너뜀도 통과로 본다
 * - 그 외는 progress
 */
export function issueVerdict(stages: readonly TcStage[], key: string, results: ResultMap): IssueVerdict {
  const items = allItems(stages).filter((i) => i.required.includes(key));
  if (!items.length) return { kind: 'none', failed: 0, decided: 0, total: 0 };
  let failed = 0;
  let decided = 0;
  let ok = 0;
  let total = 0;
  for (const item of items) {
    for (const p of item.platforms) {
      total++;
      const r = results.get(cellKey(item.id, p))?.result ?? null;
      if (r) decided++;
      if (r === 'fail') failed++;
      if (r === 'pass' || (r === 'skip' && item.optional)) ok++;
    }
  }
  const kind = failed > 0 ? 'failed' : ok === total ? 'done' : 'progress';
  return { kind, failed, decided, total };
}

/** 항목의 칸 중 결과가 없는 칸이 있는지(플랫폼 필터 반영) */
export const itemPending = (item: TcItem, results: ResultMap, platform: Platform | null) =>
  item.platforms.filter((p) => !platform || p === platform).some((p) => !results.get(cellKey(item.id, p))?.result);

export const STATUS_COLOR: Record<string, string> = {
  ready: 'default',
  'in-progress': 'processing',
  'code-review': 'purple',
  acceptance: 'warning',
  done: 'success',
};
